import React, {useEffect, useRef} from "react";
import AirportSelector from "../selector/airportSelector";
import TravelDateSelector from "../selector/travelDateSelector";
import CategorySelector from "../selector/categorySelector";
import DestinationSelector from "../selector/destinationSelector";
import {SelectorType} from "../../models/FlightSearch";
import {FlightSearchData} from "../../models/flightSearchData";

import {useTranslation} from "../../i18n/LanguageProvider";

interface Props {
    embedded?: boolean;
    activeSelector: SelectorType | null;
    setActiveSelector: (selector: SelectorType | null) => void;
    flightSearchData: FlightSearchData;
    setFlightSearchData: React.Dispatch<React.SetStateAction<FlightSearchData>>;
}

const FlightSearchSelection: React.FC<Props> = ({
                                                    embedded = false,
                                                    activeSelector,
                                                    setActiveSelector,
                                                    flightSearchData,
                                                    setFlightSearchData,
                                                }) => {
    const {t} = useTranslation();
    const panelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!embedded && window.matchMedia("(max-width: 767px)").matches) {
            panelRef.current?.scrollIntoView({block: "start"});
        }
    }, [activeSelector, embedded]);

    return (
        <div ref={panelRef} className="search-selection w-full min-w-0 max-w-3xl scroll-mt-4">

            {activeSelector === SelectorType.Airport && (
                <AirportSelector
                    compact={embedded}
                    selectedAirports={flightSearchData.airports}
                    selectedCountries={flightSearchData.departureCountries}
                    setSelectedAirports={(airports) =>
                        setFlightSearchData((prev) => ({
                            ...prev,
                            airports,
                            destinationAirports: [],
                            destinationCountries: [],
                        }))
                    }
                    setSelectedCountries={(departureCountries) =>
                        setFlightSearchData((prev) => ({
                            ...prev,
                            departureCountries,
                            destinationAirports: [],
                            destinationCountries: [],
                        }))
                    }
                />
            )}
            {activeSelector === SelectorType.Dates && (
                <TravelDateSelector
                    compact={embedded}
                    dateFrom={flightSearchData.dateFrom}
                    dateTo={flightSearchData.dateTo}
                    setDates={(from, to) =>
                        setFlightSearchData((prev) => ({...prev, dateFrom: from, dateTo: to}))
                    }
                    tripType={flightSearchData.tripType}
                    setTripType={(tripType) =>
                        setFlightSearchData((prev) => ({...prev, tripType}))
                    }
                    travelDuration={flightSearchData.travelDuration}
                    setTravelDuration={(duration) =>
                        setFlightSearchData((prev) => ({...prev, travelDuration: duration}))
                    }
                    minDurationDays={flightSearchData.minDurationDays}
                    maxDurationDays={flightSearchData.maxDurationDays}
                    setCustomDurationRange={(min, max) =>
                        setFlightSearchData((prev) => ({
                            ...prev,
                            minDurationDays: min,
                            maxDurationDays: max,
                        }))
                    }
                    mode={flightSearchData.dateSelectorMode}
                    setMode={(mode) =>
                        setFlightSearchData((prev) => ({...prev, dateSelectorMode: mode})) // <-- neu
                    }
                />
            )}

            {activeSelector === SelectorType.Destination && (
                <DestinationSelector
                    travelProfile={flightSearchData.travelProfile}
                    setTravelProfile={(travelProfile) => setFlightSearchData(prev => ({...prev, travelProfile}))}
                    departureAirportCodes={flightSearchData.airports.map((airport) => airport.code)}
                    connectionMode={flightSearchData.connectionMode}
                    setConnectionMode={(connectionMode) =>
                        setFlightSearchData((prev) => ({
                            ...prev,
                            connectionMode,
                            destinationAirports: [],
                            destinationCountries: [],
                        }))
                    }
                    selectedAirports={flightSearchData.destinationAirports}
                    selectedCountries={flightSearchData.destinationCountries}
                    setSelectedAirports={(destinationAirports) =>
                        setFlightSearchData((prev) => ({...prev, destinationAirports}))
                    }
                    setSelectedCountries={(destinationCountries) =>
                        setFlightSearchData((prev) => ({...prev, destinationCountries}))
                    }
                    onClose={() => setActiveSelector(null)}
                />
            )}

            {activeSelector === SelectorType.Category && (
                <CategorySelector
                    selectedCategories={flightSearchData.categories}
                    setSelectedCategories={(categories) =>
                        setFlightSearchData((prev) => ({...prev, categories}))
                    }
                />
            )}
            {!embedded && <button
                type="button"
                onClick={() => setActiveSelector(null)}
                className="mt-3 min-h-[44px] w-full rounded-xl bg-accent px-4 py-3 font-bold text-white md:hidden"
            >
                {t("common.done")}
            </button>}
        </div>
    );
};

export default FlightSearchSelection;
